import React, { useState, useMemo } from "react";
import { usePatientDocuments } from "@/hooks/usePatientDocuments";
import type { DocumentType } from "@/lib/api";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ExpandedCardDialog } from "@/components/consultation/dialogs/ExpandedCardDialog";
import { EmptyState } from "@/components/consultation/EmptyState";
import { DocumentFileCard } from "@/components/consultation/shared/DocumentFileCard";
import {
  Widget,
  WidgetHeader,
  WidgetContent,
} from "@/components/consultation/shared/Widget";
import { cn } from "@/lib/utils";

interface ReportsUploadsCardProps {
  patientId: string;
  onOpenDocument?: (doc: any) => void;
}

const formatMonthYearLabel = (dateStr: string) => {
  const date = new Date(dateStr);
  return date.toLocaleDateString("en-IN", { month: "long", year: "numeric" });
};

const getMonthYearKey = (dateStr: string) => {
  const date = new Date(dateStr);
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`;
};

type TabKey = "all" | "lab_reports" | "imaging" | "medicines" | "prescriptions";

const EMPTY_COPY: Record<TabKey, string> = {
  all: "No documents uploaded",
  lab_reports: "No lab reports uploaded",
  imaging: "No imaging scans uploaded",
  medicines: "No medicine documents uploaded",
  prescriptions: "No past prescriptions uploaded",
};

const tabs: { key: TabKey; label: string }[] = [
  { key: "all", label: "All" },
  { key: "lab_reports", label: "Lab Reports" },
  { key: "imaging", label: "Imaging" },
  { key: "medicines", label: "Medicines" },
  { key: "prescriptions", label: "Prescriptions" },
];

const ALL_TYPES: DocumentType[] = ["LR", "SC", "MD", "PR"];

export const ReportsUploadsCard = ({
  patientId,
  onOpenDocument,
}: ReportsUploadsCardProps) => {
  const [activeTab, setActiveTab] = useState<TabKey>("all");
  const [isExpanded, setIsExpanded] = useState(false);

  const typeFilter = useMemo((): DocumentType[] => {
    if (activeTab === "lab_reports") return ["LR"];
    if (activeTab === "imaging") return ["SC"];
    if (activeTab === "medicines") return ["MD"];
    if (activeTab === "prescriptions") return ["PR"];
    return [];
  }, [activeTab]);

  const { data: rawDocuments = [], isLoading } = usePatientDocuments(
    patientId,
    {
      types: typeFilter,
      consolidated: isExpanded,
    }
  );

  const documents = useMemo(() => {
    if (activeTab === "all") {
      return rawDocuments.filter((doc) =>
        ALL_TYPES.includes(doc.document_type)
      );
    }
    return rawDocuments;
  }, [rawDocuments, activeTab]);

  const groupedByMonth = useMemo(() => {
    const groups: Record<string, typeof documents> = {};
    documents.forEach((doc) => {
      const key = getMonthYearKey(doc.updated_at);
      if (!groups[key]) groups[key] = [];
      groups[key].push(doc);
    });
    const sortedKeys = Object.keys(groups).sort((a, b) => b.localeCompare(a));
    return sortedKeys.map((key) => ({
      date: key,
      label: formatMonthYearLabel(groups[key][0].updated_at),
      documents: groups[key],
    }));
  }, [documents]);

  const TabBar = () => (
    <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as TabKey)}>
      <TabsList className="h-auto p-0.5 w-fit">
        {tabs.map((tab) => (
          <TabsTrigger
            key={tab.key}
            value={tab.key}
            className="z-caption px-3 py-1 rounded-full"
          >
            {tab.label}
          </TabsTrigger>
        ))}
      </TabsList>
    </Tabs>
  );

  const DocumentGrid = ({ compact = true }: { compact?: boolean }) => {
    if (isLoading) {
      return (
        <div className="flex items-center justify-center h-full">
          <span className="z-body text-muted-foreground">Loading...</span>
        </div>
      );
    }
    if (documents.length === 0) {
      return <EmptyState title={EMPTY_COPY[activeTab]} variant="compact" />;
    }
    return (
      <div className="h-full overflow-y-auto scrollbar-thin space-y-3">
        {groupedByMonth.map((group) => (
          <div key={group.date}>
            <div className="flex items-center gap-2 mb-2">
              <div className="flex-1 h-px bg-border/60" />
              <span className="z-eyebrow text-foreground/60 whitespace-nowrap px-2.5 py-0.5 rounded-full border border-border/60 bg-muted/50">
                {group.label}
              </span>
              <div className="flex-1 h-px bg-border/60" />
            </div>
            <div
              className={cn(
                "px-1 grid gap-2",
                compact ? "grid-cols-4" : "grid-cols-6"
              )}
            >
              {group.documents.map((doc) => (
                <DocumentFileCard
                  key={doc.id}
                  id={doc.id}
                  fileUrl={doc.file}
                  name={doc.description || ""}
                  documentType={doc.document_type}
                  compact={compact}
                  onClick={() => {
                    onOpenDocument?.(doc);
                    if (!compact) setIsExpanded(false);
                  }}
                />
              ))}
            </div>
          </div>
        ))}
      </div>
    );
  };

  return (
    <>
      <Widget grow className="h-full">
        <WidgetHeader title="Uploads" onMaximize={() => setIsExpanded(true)} />
        <div className="px-3 pt-1 pb-2 shrink-0">
          <TabBar />
        </div>
        <WidgetContent className="flex-1 min-h-0 overflow-hidden p-0 px-3 pb-3">
          <DocumentGrid compact />
        </WidgetContent>
      </Widget>

      <ExpandedCardDialog
        open={isExpanded}
        onOpenChange={setIsExpanded}
        title="Uploads"
      >
        <div className="flex flex-col h-full gap-3">
          <TabBar />
          <div className="flex-1 min-h-0">
            <DocumentGrid compact={false} />
          </div>
        </div>
      </ExpandedCardDialog>
    </>
  );
};
